import { useMemo } from "react";
import { Box, Center, Flex, Group, Loader, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBuilding, IconRosetteDiscountCheckFilled } from "@tabler/icons-react";
import InterestRequestTableActionsMenu from "@/pages/interests/components/InterestRequestTableActionsMenu";
import InterestIconDecisionButtons from "@/pages/interests/components/InterestIconDecisionButtons";
import InterestViewProfileIconButton from "@/pages/interests/components/InterestViewProfileIconButton";
import TableRowLinkOverlay from "@/components/shared/TableRowLinkOverlay";
import TableRowLinkTableStyles from "@/components/shared/TableRowLinkTableStyles";
import {
  CollaborationStatus,
  getCollaborationRoleLabel,
  getIndustryLabel,
} from "@/components/shared/constants/general.const";
import InterestRequestActions from "@/pages/interests/components/InterestRequestActions";
import { buildCompanyDiscoverPath, companyProfileAriaLabel } from "@/shared/utils/companyProfileLink.util";
import {
  buildDashboardReturnToForInterestRow,
  type RequestFilter,
} from "@/pages/dashboard/dashboardTab.const";
import { workspaceBackLinkState } from "@/shared/utils/workspaceBackNavigation.util";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";
import { industryPillStyleForKey } from "@/pages/interests/components/interestRequestsTable.util";
import "./InterestRequestsTable.css";

type CompanyInfo = {
  id: string;
  gtid: string;
  legalName?: string;
  headquartersCountry?: string;
  industryCategory?: string[];
  status?: string;
};

export type InterestRequestListSource = "received" | "sent" | "suggested";

const LIST_SOURCE_LABEL: Record<InterestRequestListSource, string> = {
  received: "Received",
  sent: "Sent",
  suggested: "Suggested",
};

const LIST_SOURCE_STATUS_CLASS: Record<InterestRequestListSource, string> = {
  received: "gt-interest-requests-table__status-pill--received",
  sent: "gt-interest-requests-table__status-pill--sent",
  suggested: "gt-interest-requests-table__status-pill--suggested",
};

export type InterestRequestTableRow = {
  rowKind: "request" | "suggested";
  listSource: InterestRequestListSource;
  matchId: string;
  company: CompanyInfo;
  collaborationStatus: string;
  isRequester: boolean;
  requesterPaid: boolean;
  receiverPaid: boolean;
  requesterSignedNDA: boolean;
  receiverSignedNDA: boolean;
  paymentDeadline?: Date | string | null;
  requesterRole: string | null;
  expectedPartnerRole: string | null;
};

type TableColumnId =
  | "company"
  | "industry"
  | "yourRole"
  | "expectedRole"
  | "location"
  | "status"
  | "actions";

const MOBILE_COLUMN_WIDTHS: Partial<Record<TableColumnId, string>> = {
  company: "58%",
  status: "24%",
  actions: "18%",
};

const TABLE_COLUMNS: {
  id: TableColumnId;
  header: string;
  width: string;
  hideOnMobile?: boolean;
}[] = [
  { id: "company", header: "Company", width: "20%" },
  { id: "industry", header: "Industry", width: "15%", hideOnMobile: true },
  { id: "yourRole", header: "Your Role", width: "13%", hideOnMobile: true },
  { id: "expectedRole", header: "Expected Partner Role", width: "15%", hideOnMobile: true },
  { id: "location", header: "Location", width: "13%", hideOnMobile: true },
  { id: "status", header: "Status", width: "10%" },
  { id: "actions", header: "Action", width: "16%" },
];

function companySubtitle(company: CompanyInfo): string {
  if (company.legalName?.trim()) {
    return formatCompanyProfileTextForDisplay(company.legalName);
  }
  return "";
}

function IndustryPill({ label, industryKey }: { label: string; industryKey: string }) {
  return (
    <span
      className="gt-interest-requests-table__industry-pill"
      style={industryPillStyleForKey(industryKey)}
      title={label}
    >
      {label}
    </span>
  );
}

function PlainRoleText({ role }: { role: string | null | undefined }) {
  if (!role?.trim()) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Text size="sm" c="slate.7" fw={500} lineClamp={2}>
      {getCollaborationRoleLabel(role)}
    </Text>
  );
}

export function toInterestRequestTableRows(
  requests: any[],
  isRequester: boolean
): InterestRequestTableRow[] {
  return (requests ?? [])
    .map((request) => {
      const requesterCompany = request.requester as CompanyInfo | undefined;
      const receiverCompany = request.receiver as CompanyInfo | undefined;
      const company: CompanyInfo | undefined = isRequester ? receiverCompany : requesterCompany;
      if (!company?.id) return null;
      return {
        rowKind: "request" as const,
        listSource: isRequester ? "sent" : "received",
        matchId: request.id,
        company,
        collaborationStatus: request.status,
        isRequester,
        requesterPaid: request.requesterPaid,
        receiverPaid: request.receiverPaid,
        requesterSignedNDA: request.requesterSignedNDA,
        receiverSignedNDA: request.receiverSignedNDA,
        paymentDeadline: request.paymentDeadline,
        requesterRole: request.requesterRole ?? null,
        expectedPartnerRole: request.expectedPartnerRole ?? null,
      };
    })
    .filter(Boolean) as InterestRequestTableRow[];
}

interface InterestRequestsTableProps {
  requestFilter?: RequestFilter;
  rows?: InterestRequestTableRow[];
  requests?: any[];
  isRequester?: boolean;
  isLoading: boolean;
  actionLoading: string | null;
  onAccept: (matchId: string) => void;
  onDecline: (matchId: string) => void;
  onMakePayment: (matchId: string) => void;
  onViewProfile: (companyId: string) => void;
  onOpenWorkspace: (matchId: string) => void;
  /** Called when the user dismisses a suggested partner row. Receives the recommendationId. */
  onDismissSuggestion?: (recommendationId: string) => void;
  /** Set of recommendationIds currently being dismissed (shows spinner on the row). */
  dismissingIds?: Set<string>;
}

function ColGroup({ columns }: { columns: typeof TABLE_COLUMNS }) {
  return (
    <colgroup>
      {columns.map((col) => (
        <col key={col.id} style={{ width: col.width }} />
      ))}
    </colgroup>
  );
}

function TableHeaderRow({ columns }: { columns: typeof TABLE_COLUMNS }) {
  return (
    <tr>
      {columns.map((col) => (
        <th
          key={col.id}
          className={
            [
              col.id === "location" ? "gt-interest-requests-table__location-header" : undefined,
              col.id === "actions" ? "gt-interest-requests-table__actions-header" : undefined,
            ]
              .filter(Boolean)
              .join(" ") || undefined
          }
        >
          {col.header}
        </th>
      ))}
    </tr>
  );
}

export default function InterestRequestsTable({
  requestFilter = "all",
  rows: rowsProp,
  requests,
  isRequester = false,
  isLoading,
  actionLoading,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
  onDismissSuggestion,
  dismissingIds,
}: InterestRequestsTableProps) {
  const isMobile = useMediaQuery("(max-width: 47.99em)");

  const data = useMemo(() => {
    if (rowsProp) return rowsProp;
    return toInterestRequestTableRows(requests ?? [], isRequester);
  }, [rowsProp, requests, isRequester]);

  const showRoleColumns = data.length === 0 || data.some((r) => r.rowKind === "request");

  const visibleColumns = useMemo(() => {
    let columns = showRoleColumns
      ? TABLE_COLUMNS
      : TABLE_COLUMNS.filter((c) => c.id !== "yourRole" && c.id !== "expectedRole");

    if (isMobile) {
      columns = columns
        .filter((c) => !c.hideOnMobile)
        .map((c) => ({
          ...c,
          width: MOBILE_COLUMN_WIDTHS[c.id] ?? c.width,
        }));
    }

    return columns;
  }, [showRoleColumns, isMobile]);

  if (isLoading) {
    return (
      <Center py="xl" style={{ flex: 1 }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Box className="gt-interest-requests-table gt-table-row-link">
      <TableRowLinkTableStyles />
      <Box className="gt-interest-requests-table__card">
        <Box className="gt-interest-requests-table__scroll-x">
          <Box className="gt-interest-requests-table__inner">
            <Box className="gt-interest-requests-table__header">
              <table aria-label="Interest requests">
                <ColGroup columns={visibleColumns} />
                <thead>
                  <TableHeaderRow columns={visibleColumns} />
                </thead>
              </table>
            </Box>

            <Box className="gt-interest-requests-table__body">
              <table>
                <ColGroup columns={visibleColumns} />
                <tbody>
                  {data.map((row) => {
                    const { company } = row;
                    const isDeclinedRequest =
                      row.rowKind === "request" &&
                      row.collaborationStatus === CollaborationStatus.RECEIVER_DECLINED;
                    const profilePath = isDeclinedRequest
                      ? `/collaborations/${row.matchId}`
                      : buildCompanyDiscoverPath(company.id);
                    const linkState = workspaceBackLinkState(
                      buildDashboardReturnToForInterestRow(
                        requestFilter,
                        row.rowKind,
                        row.listSource
                      )
                    );
                    const industryKey = company.industryCategory?.[0] ?? "";
                    const industryLabel =
                      industryKey && Array.isArray(company.industryCategory)
                        ? getIndustryLabel(company.industryCategory[0])
                        : null;
                    const location = company.headquartersCountry?.trim();
                    const locationFlag = location ? flagEmojiFromHeadquartersCountry(location) : null;

                    return (
                      <tr key={`${row.rowKind}-${row.matchId}`} data-row-link="">
                        {visibleColumns.map((col) => {
                          if (col.id === "company") {
                            const subtitle = companySubtitle(company);
                            return (
                              <td
                                key={col.id}
                                data-row-link-host=""
                                style={{ position: "relative", overflow: "visible" }}
                              >
                                <TableRowLinkOverlay
                                  to={profilePath}
                                  ariaLabel={companyProfileAriaLabel(company.gtid)}
                                  linkState={linkState}
                                />
                                <Group gap="sm" wrap="nowrap" align="flex-start">
                                  {!isMobile ? (
                                    <Box className="gt-interest-requests-table__company-icon">
                                      <IconBuilding size={20} stroke={1.75} />
                                    </Box>
                                  ) : null}
                                  <Box style={{ minWidth: 0 }}>
                                    <Group gap={6} wrap="nowrap" align="center">
                                      <Text size="sm" fw={700} c="#0f172a" lineClamp={1}>
                                        {company.gtid || "N/A"}
                                      </Text>
                                      {company.status === "VERIFIED" && (
                                        <IconRosetteDiscountCheckFilled
                                          size={16}
                                          style={{
                                            color: "var(--mantine-color-primary-6)",
                                            flexShrink: 0,
                                          }}
                                        />
                                      )}
                                    </Group>
                                    {subtitle ? (
                                      <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
                                        {subtitle}
                                      </Text>
                                    ) : null}
                                  </Box>
                                </Group>
                              </td>
                            );
                          }

                          if (col.id === "industry") {
                            const industryDisplayLabel = industryLabel
                              ? company.industryCategory!.length > 1
                                ? `${industryLabel} +${company.industryCategory!.length - 1}`
                                : industryLabel
                              : null;
                            return (
                              <td key={col.id}>
                                {industryDisplayLabel && industryKey ? (
                                  <IndustryPill
                                    label={industryDisplayLabel}
                                    industryKey={industryKey}
                                  />
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                )}
                              </td>
                            );
                          }

                          if (col.id === "yourRole") {
                            return (
                              <td key={col.id}>
                                <PlainRoleText
                                  role={row.rowKind === "suggested" ? null : row.requesterRole}
                                />
                              </td>
                            );
                          }

                          if (col.id === "expectedRole") {
                            return (
                              <td key={col.id}>
                                <PlainRoleText role={row.expectedPartnerRole} />
                              </td>
                            );
                          }

                          if (col.id === "location") {
                            return (
                              <td key={col.id} className="gt-interest-requests-table__location-cell">
                                {!location ? (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                ) : (
                                  <Flex
                                    direction="column"
                                    gap={6}
                                    align="flex-start"
                                    w="100%"
                                    maw="100%"
                                    style={{ minWidth: 0 }}
                                  >
                                    {locationFlag}
                                    <Text
                                      component="span"
                                      size="sm"
                                      className="gt-interest-requests-table__location-country"
                                      w="100%"
                                      maw="100%"
                                      style={{
                                        minWidth: 0,
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                        whiteSpace: "normal",
                                      }}
                                    >
                                      {location}
                                    </Text>
                                  </Flex>
                                )}
                              </td>
                            );
                          }

                          if (col.id === "status") {
                            const isReceiverDeclined = row.collaborationStatus === CollaborationStatus.RECEIVER_DECLINED;
                            const statusLabel = isReceiverDeclined
                              ? row.isRequester
                                ? "Declined"
                                : "Declined By You"
                              : LIST_SOURCE_LABEL[row.listSource];
                            const statusClass = isReceiverDeclined
                              ? "gt-interest-requests-table__status-pill--declined"
                              : LIST_SOURCE_STATUS_CLASS[row.listSource];
                            return (
                              <td key={col.id}>
                                <span
                                  className={`gt-interest-requests-table__status-pill ${statusClass}`}
                                >
                                  {statusLabel}
                                </span>
                              </td>
                            );
                          }

                          if (col.id === "actions") {
                            return (
                              <td key={col.id} data-action-cell="" style={{ position: "relative", zIndex: 2 }}>
                                <Box data-surface-action>
                                  {isMobile ? (
                                    row.rowKind === "suggested" ? (
                                      <InterestRequestTableActionsMenu
                                        rowKind="suggested"
                                        onViewProfile={() => onViewProfile(company.id)}
                                        onAcceptSuggestion={
                                          onDismissSuggestion
                                            ? () => onViewProfile(company.id)
                                            : undefined
                                        }
                                        onRejectSuggestion={
                                          onDismissSuggestion
                                            ? () => onDismissSuggestion(row.matchId)
                                            : undefined
                                        }
                                        isLoading={dismissingIds?.has(row.matchId) ?? false}
                                      />
                                    ) : (
                                      <InterestRequestTableActionsMenu
                                        rowKind="request"
                                        matchId={row.matchId}
                                        companyId={company.id}
                                        collaborationStatus={row.collaborationStatus}
                                        isRequester={row.isRequester}
                                        requesterPaid={row.requesterPaid}
                                        receiverPaid={row.receiverPaid}
                                        requesterSignedNDA={row.requesterSignedNDA}
                                        receiverSignedNDA={row.receiverSignedNDA}
                                        paymentDeadline={row.paymentDeadline}
                                        onAccept={onAccept}
                                        onDecline={onDecline}
                                        onMakePayment={onMakePayment}
                                        onViewProfile={onViewProfile}
                                        onOpenWorkspace={onOpenWorkspace}
                                        isLoading={actionLoading === row.matchId}
                                      />
                                    )
                                  ) : row.rowKind === "suggested" ? (
                                    <Group gap={6} wrap="nowrap" align="center">
                                      <InterestViewProfileIconButton
                                        onClick={() => onViewProfile(company.id)}
                                        size="lg"
                                      />
                                      {onDismissSuggestion ? (
                                        <InterestIconDecisionButtons
                                          acceptLabel="Accept suggestion"
                                          acceptTitle="Accept partner suggestion?"
                                          acceptMessage="Do you want to accept this partner suggestion?"
                                          rejectLabel="Reject suggestion"
                                          rejectTitle="Reject partner suggestion?"
                                          rejectMessage="Do you want to reject this partner suggestion?"
                                          onAccept={() => onViewProfile(company.id)}
                                          onReject={() => onDismissSuggestion(row.matchId)}
                                          isLoading={dismissingIds?.has(row.matchId) ?? false}
                                          size="lg"
                                        />
                                      ) : null}
                                    </Group>
                                  ) : (
                                    <InterestRequestActions
                                      matchId={row.matchId}
                                      companyId={company.id}
                                      collaborationStatus={row.collaborationStatus}
                                      isRequester={row.isRequester}
                                      requesterPaid={row.requesterPaid}
                                      receiverPaid={row.receiverPaid}
                                      requesterSignedNDA={row.requesterSignedNDA}
                                      receiverSignedNDA={row.receiverSignedNDA}
                                      paymentDeadline={row.paymentDeadline}
                                      onAccept={onAccept}
                                      onDecline={onDecline}
                                      onMakePayment={onMakePayment}
                                      onViewProfile={onViewProfile}
                                      onOpenWorkspace={onOpenWorkspace}
                                      isLoading={actionLoading === row.matchId}
                                      variant="table"
                                    />
                                  )}
                                </Box>
                              </td>
                            );
                          }

                          return <td key={col.id} />;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
